// ============================================================================
// StartingServicesFields — "starting this building starts these services".
//
// Ben, 2026-10-23: a started building that is not paused always has a running
// service. Starting a one-service building starts that service — nothing to
// choose. Starting a building with several starts all of them by default; the
// person unticks one that is not starting yet (the exception), says why, and at
// least one stays ticked. confirm_account_started() enforces the same rules, so
// this screen only says them out loud before the database would.
//
// The Account start card and the overdue resolution window both render this,
// and both send the payload from here. ⛔ A CALLER NEVER ASSEMBLES THE LIST —
// one copy of the rule, the same way StartDateMoveFields owns the moved path.
//
// ⛔ EVERY EXPORT IS DECLARED AT MODULE SCOPE (TCSD-0232): a component declared
// inside another remounts on every render and the reason box loses focus.
// ============================================================================

import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '../../shared/supabase';
import { errorMessage } from '../../shared/errors';
import { notStartedReason } from '../account-management/lib/not-started';

export interface StartingService {
  assignmentId: string;
  service: string;
  price: string;
  sub: string;
  /** Already marked "not started" before the building opened. */
  alreadyWaiting: boolean;
}

export interface NotStartingItem {
  assignment_id: string;
  reason: string;
}

const money = (n: number) =>
  `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function priceOf(method: string | null, rate: number | null): string {
  if (method === 'Variable') return 'billed on hours worked';
  if (rate == null) return 'client rate not set';
  return method === 'Hourly' ? `client ${money(rate)}/hr` : `client ${money(rate)}/mo`;
}

/** Pure: what the person ticked → the list the database takes, and whether it can go. */
export function startingServicesPayload(
  lines: readonly StartingService[],
  off: Readonly<Record<string, string | undefined>>,
): { items: NotStartingItem[]; ready: boolean; problem: string | null } {
  const items = lines
    .filter((l) => off[l.assignmentId] !== undefined)
    .map((l) => ({ assignment_id: l.assignmentId, reason: (off[l.assignmentId] ?? '').trim() }));
  if (lines.length > 1 && items.length >= lines.length) {
    return {
      items,
      ready: false,
      problem: 'At least one service has to start, or the building hasn’t started. Tick one, or use "The start date moved".',
    };
  }
  if (items.some((i) => i.reason === '')) {
    return { items, ready: false, problem: 'Say why each unticked service isn’t starting.' };
  }
  return { items, ready: true, problem: null };
}

export function useStartingServices(facilityId: string | null | undefined) {
  const [lines, setLines] = useState<StartingService[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  /** Lines paused with the building — shown only when nothing else would start. */
  const [pausedCount, setPausedCount] = useState(0);
  /** assignment_id → reason, present only for an unticked line. */
  const [off, setOff] = useState<Record<string, string | undefined>>({});

  const load = useCallback(async () => {
    if (!facilityId) {
      setLines([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    const { data, error: e } = await supabase
      .from('service_assignments')
      .select('assignment_id, service_code, billing_method, billing_rate, provider_id, worker_name, assignment_status, start_date, notes')
      .eq('facility_id', facilityId)
      .in('assignment_status', ['Active', 'Scheduled'])
      .order('assignment_id');
    if (e) {
      setError(errorMessage(e, 'Could not load this building’s services.'));
      setLoading(false);
      return;
    }
    const rows = (data ?? []) as Array<{
      assignment_id: string; service_code: string | null; billing_method: string | null;
      billing_rate: number | null; provider_id: string | null; worker_name: string | null;
      assignment_status: string; start_date: string | null; notes: string | null;
    }>;
    if (rows.length === 0) {
      const { count } = await supabase
        .from('service_assignments')
        .select('assignment_id', { count: 'exact', head: true })
        .eq('facility_id', facilityId)
        .eq('assignment_status', 'Suspended');
      setPausedCount(count ?? 0);
    } else {
      setPausedCount(0);
    }
    const codes = [...new Set(rows.map((r) => r.service_code).filter((c): c is string => !!c))];
    const subs = [...new Set(rows.map((r) => r.provider_id).filter((p): p is string => !!p))];
    const [cat, prov] = await Promise.all([
      codes.length
        ? supabase.from('service_catalog').select('service_code, display_name').in('service_code', codes)
        : Promise.resolve({ data: [], error: null }),
      subs.length
        ? supabase.from('service_providers').select('provider_id, partner_name').in('provider_id', subs)
        : Promise.resolve({ data: [], error: null }),
    ]);
    if (cat.error || prov.error) {
      setError(errorMessage(cat.error ?? prov.error, 'Could not load this building’s services.'));
      setLoading(false);
      return;
    }
    const name = new Map((cat.data ?? []).map((c: { service_code: string; display_name: string | null }) => [c.service_code, c.display_name]));
    const subName = new Map((prov.data ?? []).map((p: { provider_id: string; partner_name: string | null }) => [p.provider_id, p.partner_name]));
    const built = rows.map((r) => ({
      assignmentId: r.assignment_id,
      service: (r.service_code && name.get(r.service_code)) || 'Service line',
      price: priceOf(r.billing_method, r.billing_rate),
      sub: (r.provider_id && subName.get(r.provider_id)) || r.worker_name || 'sub not recorded',
      alreadyWaiting: r.assignment_status === 'Scheduled' && !r.start_date,
    }));
    setLines(built);
    // A line already marked "not started" stays unticked, with its own reason.
    const seed: Record<string, string | undefined> = {};
    if (built.length > 1) {
      for (const r of rows) {
        if (r.assignment_status === 'Scheduled' && !r.start_date) {
          seed[r.assignment_id] = notStartedReason(r.notes)?.reason ?? '';
        }
      }
    }
    setOff(seed);
    setLoading(false);
  }, [facilityId]);

  useEffect(() => {
    void load();
  }, [load]);

  const toggle = useCallback((id: string) => {
    setOff((o) => {
      const next = { ...o };
      if (next[id] === undefined) next[id] = '';
      else delete next[id];
      return next;
    });
  }, []);
  const setReason = useCallback((id: string, reason: string) => {
    setOff((o) => ({ ...o, [id]: reason }));
  }, []);

  const result = useMemo(() => startingServicesPayload(lines, off), [lines, off]);
  return { lines, pausedCount, loading, error, off, toggle, setReason, reload: load, ...result };
}

export type StartingServicesState = ReturnType<typeof useStartingServices>;

export function StartingServicesFields({ state }: { state: StartingServicesState }) {
  const { lines, pausedCount, loading, error, off, toggle, setReason, problem } = state;
  if (loading) return <div className="text-xs text-tcs-text-muted">Loading this building&rsquo;s services…</div>;
  if (error) return <div className="text-xs text-st-danger-fg">{error}</div>;

  if (lines.length === 0 && pausedCount > 0) {
    return (
      <div className="rounded-lg border border-st-warn-border bg-st-warn-bg px-3 py-2 text-xs text-st-warn-fg">
        This building is paused, and its service is paused with it, so nothing would start. Resume the building
        first, then confirm it started.
      </div>
    );
  }

  if (lines.length === 0) {
    return (
      <div className="rounded-lg border border-st-warn-border bg-st-warn-bg px-3 py-2 text-xs text-st-warn-fg">
        This building has no service on it, so nothing would start. Add its service line on the building card first.
        (A building whose work is billed under another, like the OPRMC ERs, is the one exception.)
      </div>
    );
  }

  if (lines.length === 1) {
    const l = lines[0];
    return (
      <div className="rounded-lg border border-tcs-border bg-tcs-surface px-3 py-2">
        <div className="text-sm font-medium text-tcs-text">Starting this building starts its service:</div>
        <div className="mt-1 text-sm text-tcs-text">✓ {l.service} — {l.price} · {l.sub}</div>
        <div className="mt-1 text-xs text-tcs-text-secondary">
          It&rsquo;s the building&rsquo;s only service, so it starts with the building. If it isn&rsquo;t starting, the
          building hasn&rsquo;t started: use &ldquo;The start date moved&rdquo; instead.
        </div>
      </div>
    );
  }

  return (
    <fieldset className="rounded-lg border border-tcs-border bg-tcs-surface px-3 py-2">
      <legend className="px-1 text-sm font-medium text-tcs-text">
        Starting this building starts these services. Untick one only if it isn&rsquo;t starting yet.
      </legend>
      <div className="mt-1 space-y-2">
        {lines.map((l) => {
          const unticked = off[l.assignmentId] !== undefined;
          return (
            <div key={l.assignmentId}>
              <label className="flex items-start gap-2 text-sm text-tcs-text">
                <input
                  type="checkbox"
                  className="mt-0.5 h-4 w-4"
                  checked={!unticked}
                  onChange={() => toggle(l.assignmentId)}
                />
                <span>
                  <span className="font-medium">{l.service}</span> — {l.price} · {l.sub}
                </span>
              </label>
              {unticked && (
                <label className="ml-6 mt-1 block">
                  <span className="text-xs font-medium text-st-warn-fg">
                    Why isn&rsquo;t it starting? It&rsquo;s the only record of what we&rsquo;re waiting on.
                  </span>
                  <input
                    type="text"
                    value={off[l.assignmentId] ?? ''}
                    onChange={(e) => setReason(l.assignmentId, e.target.value)}
                    placeholder="e.g. waiting on the new clinic's census"
                    className="mt-1 w-full rounded-md border border-tcs-border-input bg-tcs-field px-3 py-1.5 text-sm text-tcs-text"
                  />
                </label>
              )}
            </div>
          );
        })}
      </div>
      {problem && <div className="mt-2 text-xs font-medium text-st-danger-fg">{problem}</div>}
    </fieldset>
  );
}
